/**
 * FormikInput — drop-in replacement for FormikTextField.
 *
 * Uses Formik's useField hook, wraps DS Input from Primitives.
 * Forwards meta.error to errorText only when meta.touched.
 * Preserves the "Required on blur" behavior of legacy FormikTextField.
 *
 * Migration mapping:
 *   <FormikTextField name="email" label="Email" />
 *     ↓
 *   <FormikInput name="email" label="Email" />
 *
 * Props that DON'T carry over (handled separately):
 *   - select → defer to Wave 4 (use Combobox/Select)
 *   - multiline/rows → use FormikTextarea instead
 *   - InputProps.startAdornment → use iconLeft
 *   - InputProps.endAdornment → use iconRight
 *   - children (MenuItem) → Wave 4
 */
import {useField} from 'formik'
import React, {forwardRef, useState} from 'react'
import {Input} from '~/src/design-system/Primitives'
import type {InputProps} from '~/src/design-system/Primitives'
import {FieldHelper, FieldLabel, FieldWrapper, RequiredIndicator} from '../FieldParts'

export interface FormikInputProps extends Omit<InputProps, 'name' | 'error'> {
  /** Formik field name — required */
  name: string
  /** Label text above the input */
  label?: string
  /** Hint text below the field (hidden when an error is shown) */
  helperText?: string
  /** Show a required indicator (*) next to the label */
  required?: boolean
  /** Custom required indicator character (defaults to "*") */
  requiredIndicator?: string
  /** Whether the field takes full width (default: true, matching FormikTextField) */
  fullWidth?: boolean
  /** Custom onChange handler — called BEFORE Formik's handler (matches legacy behavior) */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Custom onBlur handler */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  /** Custom data-testid (defaults to "formik-input-{name}") */
  'data-testid'?: string
  /** MUI sx prop — ignored, present for migration compat (allows gradual removal) */
  sx?: unknown
  /** MUI inputProps — spread onto the input for maxLength etc. */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
}

export const FormikInput = forwardRef<HTMLInputElement, FormikInputProps>(
  (
    {
      name,
      label,
      helperText,
      required,
      requiredIndicator = '*',
      fullWidth = true,
      onChange: customOnChange,
      onBlur: customOnBlur,
      inputProps,
      sx: _sx, // absorbed, not used
      'data-testid': testId,
      id,
      ...rest
    },
    ref,
  ) => {
    const [field, meta] = useField(name)
    const [resolvedId] = useState(() => id ?? `formik-input-${name}`)
    const hasError = meta.touched && Boolean(meta.error)
    // Match legacy FormikTextField: suppress 'DoNotShow' errors
    const errorText = hasError && meta.error !== 'DoNotShow' ? meta.error : undefined

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Custom handler first, then Formik — matches legacy FormikTextField order
      customOnChange?.(e)
      field.onChange(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      customOnBlur?.(e)
      field.onBlur(e)
    }

    return (
      <FieldWrapper $fullWidth={fullWidth}>
        {label && (
          <FieldLabel htmlFor={resolvedId}>
            {label}
            {required && (
              <RequiredIndicator aria-hidden="true"> {requiredIndicator}</RequiredIndicator>
            )}
          </FieldLabel>
        )}
        <Input
          ref={ref}
          id={resolvedId}
          name={name}
          value={field.value ?? ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={hasError}
          aria-describedby={errorText ? `${resolvedId}-error` : undefined}
          required={required}
          data-testid={testId ?? `formik-input-${name}`}
          {...inputProps}
          {...rest}
        />
        {errorText && (
          <FieldHelper id={`${resolvedId}-error`} $error role="alert">
            {errorText}
          </FieldHelper>
        )}
        {!errorText && helperText && <FieldHelper>{helperText}</FieldHelper>}
      </FieldWrapper>
    )
  },
)
FormikInput.displayName = 'FormikInput'

export default FormikInput
